import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import type { SimulationMesh } from '@/simulation/mesh';
import type { CreasePattern } from '@/origami/types';
import { assignmentColor } from '@/origami/types';
import type { ColorMode, ViewSettings } from '@/state/store';

/**
 * Three.js scene for the folded model.
 *
 * Three.js is a *view* here and nothing more: it never owns state. Every frame
 * the worker hands us a Float32Array of positions and we copy it straight into
 * the existing BufferAttribute - no object churn, no geometry rebuilds unless
 * the topology itself changed.
 *
 * Front and back of the paper are two meshes over one geometry, with FrontSide
 * and BackSide materials in different tones. That keeps the orientation readable
 * without the shader gymnastics that a single double-sided material would need.
 */
export interface SceneHandles {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  orthoCamera: THREE.OrthographicCamera;
  controls: OrbitControls;
  dispose: () => void;
  setMesh: (mesh: SimulationMesh, pattern: CreasePattern) => void;
  updatePositions: (
    positions: Float32Array,
    strain: Float32Array,
    collisionFlags: Uint8Array,
  ) => void;
  applyView: (view: ViewSettings) => void;
  render: () => void;
  resize: (width: number, height: number) => void;
  setCameraPreset: (preset: CameraPreset) => void;
  pick: (ndc: THREE.Vector2) => PickResult | null;
  highlight: (vertex: number | null) => void;
  /** World point -> the paper's own coordinate space. */
  toModelSpace: (world: THREE.Vector3) => THREE.Vector3;
  setControlsEnabled: (enabled: boolean) => void;
  /** Current bounding sphere radius, used to frame the camera. */
  radius: number;
}

export type CameraPreset = 'reset' | 'front' | 'back' | 'top' | 'iso' | 'left' | 'right';

export interface PickResult {
  triangle: number;
  vertex: number;
  /** Hit point in the model's local (paper) space. */
  point: THREE.Vector3;
  /** Hit point in world space, for setting up a drag plane. */
  worldPoint: THREE.Vector3;
}

const FRONT_COLOR = new THREE.Color('#f2ece1');
const BACK_COLOR = new THREE.Color('#cdd4de');

/** Perceptually ordered strain ramp; the legend in the UI uses the same stops. */
const STRAIN_STOPS: [number, THREE.Color][] = [
  [0.0, new THREE.Color('#2b6cb0')],
  [0.005, new THREE.Color('#38a169')],
  [0.01, new THREE.Color('#d69e2e')],
  [0.02, new THREE.Color('#dd6b20')],
  [0.05, new THREE.Color('#c53030')],
];

export function strainColor(value: number, out = new THREE.Color()): THREE.Color {
  if (!Number.isFinite(value)) return out.set('#888888');
  if (value <= STRAIN_STOPS[0][0]) return out.copy(STRAIN_STOPS[0][1]);
  for (let i = 1; i < STRAIN_STOPS.length; i++) {
    if (value <= STRAIN_STOPS[i][0]) {
      const [x0, c0] = STRAIN_STOPS[i - 1];
      const [x1, c1] = STRAIN_STOPS[i];
      const t = (value - x0) / (x1 - x0);
      return out.copy(c0).lerp(c1, t);
    }
  }
  return out.copy(STRAIN_STOPS[STRAIN_STOPS.length - 1][1]);
}

export function createPaperScene(canvas: HTMLCanvasElement): SceneHandles {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    powerPreference: 'high-performance',
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  const scene = new THREE.Scene();

  const camera = new THREE.PerspectiveCamera(38, 1, 0.01, 1000);
  camera.position.set(1.6, -1.9, 1.5);
  camera.up.set(0, 0, 1);

  const orthoCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, -100, 100);
  orthoCamera.position.copy(camera.position);
  orthoCamera.up.set(0, 0, 1);

  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.12;
  controls.screenSpacePanning = true;

  const key = new THREE.DirectionalLight(0xffffff, 2.1);
  key.position.set(2.5, -3, 4);
  key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  key.shadow.camera.near = 0.1;
  key.shadow.camera.far = 20;
  key.shadow.bias = -0.0012;
  scene.add(key);

  const fill = new THREE.DirectionalLight(0xdfe8ff, 0.55);
  fill.position.set(-3, 2, 1.5);
  scene.add(fill);
  scene.add(new THREE.HemisphereLight(0xdce6ff, 0x30343d, 0.75));

  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(40, 40),
    new THREE.ShadowMaterial({ opacity: 0.22 }),
  );
  ground.receiveShadow = true;
  scene.add(ground);

  /**
   * Replaced wholesale whenever the topology changes.
   *
   * Swapping `geometry.index` and the attributes in place on a live geometry
   * leaves three.js's vertex-array cache pointing at the previous index buffer,
   * and the draw call then fails with INVALID_OPERATION - the mesh silently
   * renders nothing while `renderer.info` still counts its triangles. Building a
   * fresh BufferGeometry per topology avoids the whole class of problem; there is
   * at most one rebuild per edit, so it costs nothing.
   */
  let geometry = new THREE.BufferGeometry();
  const frontMaterial = new THREE.MeshStandardMaterial({
    color: FRONT_COLOR,
    roughness: 0.82,
    metalness: 0.0,
    side: THREE.FrontSide,
    flatShading: true,
  });
  const backMaterial = new THREE.MeshStandardMaterial({
    color: BACK_COLOR,
    roughness: 0.88,
    metalness: 0.0,
    side: THREE.BackSide,
    flatShading: true,
  });
  const frontMesh = new THREE.Mesh(geometry, frontMaterial);
  const backMesh = new THREE.Mesh(geometry, backMaterial);
  frontMesh.castShadow = true;
  frontMesh.receiveShadow = true;

  /**
   * Everything the solver produces lives in *paper* coordinates, whose origin is
   * a corner of the sheet, not its middle. Rather than translating the data (and
   * breaking every index the analysis panels quote), the whole model hangs off a
   * group that is offset by the flat sheet's centre, so orbiting is about the
   * paper and not about a corner.
   */
  const model = new THREE.Group();
  model.add(frontMesh, backMesh);
  scene.add(model);

  let creaseGeometry = new THREE.BufferGeometry();
  const creaseMaterial = new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.95 });
  const creaseLines = new THREE.LineSegments(creaseGeometry, creaseMaterial);
  model.add(creaseLines);

  const wireGeometry = new THREE.WireframeGeometry();
  const wireMaterial = new THREE.LineBasicMaterial({ color: 0x6b7482, transparent: true, opacity: 0.35 });
  const wireLines = new THREE.LineSegments(wireGeometry, wireMaterial);
  wireLines.visible = false;
  model.add(wireLines);

  const highlightGeometry = new THREE.SphereGeometry(1, 16, 12);
  const highlightMaterial = new THREE.MeshBasicMaterial({ color: 0x4c8dff });
  const highlightMesh = new THREE.Mesh(highlightGeometry, highlightMaterial);
  highlightMesh.visible = false;
  model.add(highlightMesh);

  const raycaster = new THREE.Raycaster();

  let mesh: SimulationMesh | null = null;
  let creaseIndexPairs: number[] = [];
  let colorAttr: THREE.BufferAttribute | null = null;
  let colorMode: ColorMode = 'normal';
  let radius = 1;
  const scratchColor = new THREE.Color();

  let theme: 'dark' | 'light' = 'dark';
  let lastPattern: CreasePattern | null = null;

  function setMesh(nextMesh: SimulationMesh, nextPattern: CreasePattern): void {
    mesh = nextMesh;
    lastPattern = nextPattern;

    const positions = new Float32Array(nextMesh.vertexCount * 3);
    for (let i = 0; i < positions.length; i++) positions[i] = nextMesh.restPositions[i];

    const previous = geometry;
    geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setIndex(new THREE.BufferAttribute(Uint32Array.from(nextMesh.triangles), 1));
    colorAttr = new THREE.BufferAttribute(new Float32Array(nextMesh.vertexCount * 3).fill(1), 3);
    geometry.setAttribute('color', colorAttr);
    frontMesh.geometry = geometry;
    backMesh.geometry = geometry;
    previous.dispose();
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    const box = geometry.boundingBox;
    if (box) {
      model.position.set(
        -(box.min.x + box.max.x) / 2,
        -(box.min.y + box.max.y) / 2,
        -(box.min.z + box.max.z) / 2,
      );
    }
    geometry.computeBoundingSphere();
    // Bounding-sphere radius about the *recentred* model, i.e. the half diagonal.
    radius = Math.max(
      box
        ? Math.hypot(box.max.x - box.min.x, box.max.y - box.min.y, box.max.z - box.min.z) / 2
        : 1,
      1e-3,
    );

    // Crease lines: one segment per real crease (auxiliary edges excluded).
    creaseIndexPairs = [];
    const creaseColors: number[] = [];
    for (let h = 0; h < nextMesh.hingeCount; h++) {
      const edge = nextMesh.hingePatternEdge[h];
      if (edge < 0) continue;
      const v1 = nextMesh.hingeVertices[h * 4];
      const v2 = nextMesh.hingeVertices[h * 4 + 1];
      creaseIndexPairs.push(v1, v2);
      const color = new THREE.Color(assignmentColor(nextPattern.edgesAssignment[edge], theme));
      creaseColors.push(color.r, color.g, color.b, color.r, color.g, color.b);
    }
    // Boundary edges have no hinge, so add them from the pattern directly.
    for (let e = 0; e < nextPattern.edgesVertices.length; e++) {
      const assignment = nextPattern.edgesAssignment[e];
      if (assignment !== 'B' && assignment !== 'C') continue;
      const [pa, pb] = nextPattern.edgesVertices[e];
      const a = findMeshVertex(nextMesh, pa);
      const b = findMeshVertex(nextMesh, pb);
      if (a < 0 || b < 0) continue;
      creaseIndexPairs.push(a, b);
      const color = new THREE.Color(assignmentColor(assignment, theme));
      creaseColors.push(color.r, color.g, color.b, color.r, color.g, color.b);
    }
    const previousCreases = creaseGeometry;
    creaseGeometry = new THREE.BufferGeometry();
    creaseGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array(creaseIndexPairs.length * 3), 3),
    );
    creaseGeometry.setAttribute(
      'color',
      new THREE.BufferAttribute(Float32Array.from(creaseColors), 3),
    );
    creaseLines.geometry = creaseGeometry;
    previousCreases.dispose();

    highlightMesh.scale.setScalar(Math.max(radius * 0.012, 1e-4));
    ground.position.z = -radius * 1.4;
    updatePositions(positions, new Float32Array(nextMesh.triangleCount), new Uint8Array(nextMesh.triangleCount));
    frameCamera();
  }

  function updatePositions(
    positions: Float32Array,
    triangleStrain: Float32Array,
    collisionFlags: Uint8Array,
  ): void {
    if (!mesh) return;
    const attr = geometry.getAttribute('position') as THREE.BufferAttribute;
    if (attr.array.length !== positions.length) return;
    (attr.array as Float32Array).set(positions);
    attr.needsUpdate = true;
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();

    // Crease line positions.
    const cattr = creaseGeometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (cattr) {
      const arr = cattr.array as Float32Array;
      for (let i = 0; i < creaseIndexPairs.length; i++) {
        const v = creaseIndexPairs[i];
        arr[i * 3] = positions[v * 3];
        arr[i * 3 + 1] = positions[v * 3 + 1];
        arr[i * 3 + 2] = positions[v * 3 + 2];
      }
      cattr.needsUpdate = true;
    }

    if (colorAttr && colorMode !== 'normal') {
      const colors = colorAttr.array as Float32Array;
      colors.fill(0.85);
      if (colorMode === 'strain' || colorMode === 'constraint') {
        const accum = new Float32Array(mesh.vertexCount);
        const counts = new Float32Array(mesh.vertexCount);
        for (let t = 0; t < mesh.triangleCount; t++) {
          const value = colorMode === 'constraint' && collisionFlags[t] ? 0.05 : triangleStrain[t];
          for (let k = 0; k < 3; k++) {
            const vi = mesh.triangles[t * 3 + k];
            accum[vi] += value;
            counts[vi]++;
          }
        }
        for (let v = 0; v < mesh.vertexCount; v++) {
          const value = counts[v] ? accum[v] / counts[v] : 0;
          strainColor(value, scratchColor);
          colors[v * 3] = scratchColor.r;
          colors[v * 3 + 1] = scratchColor.g;
          colors[v * 3 + 2] = scratchColor.b;
        }
      } else if (colorMode === 'orientation') {
        for (let v = 0; v < mesh.vertexCount; v++) {
          const piece = mesh.vertexPiece[v] / Math.max(1, mesh.pieceCount);
          scratchColor.setHSL(0.55 + piece * 0.3, 0.35, 0.62);
          colors[v * 3] = scratchColor.r;
          colors[v * 3 + 1] = scratchColor.g;
          colors[v * 3 + 2] = scratchColor.b;
        }
      }
      colorAttr.needsUpdate = true;
    }

    if (wireLines.visible) {
      wireLines.geometry.dispose();
      wireLines.geometry = new THREE.WireframeGeometry(geometry);
    }
  }

  function applyView(view: ViewSettings): void {
    colorMode = view.colorMode;
    const useVertexColors = view.colorMode !== 'normal';
    if (frontMaterial.vertexColors !== useVertexColors) {
      frontMaterial.vertexColors = useVertexColors;
      backMaterial.vertexColors = useVertexColors;
      frontMaterial.needsUpdate = true;
      backMaterial.needsUpdate = true;
    }
    frontMaterial.color.copy(useVertexColors ? new THREE.Color(0xffffff) : FRONT_COLOR);
    backMaterial.color.copy(useVertexColors ? new THREE.Color(0xffffff) : BACK_COLOR);
    frontMaterial.transparent = view.transparent;
    backMaterial.transparent = view.transparent;
    frontMaterial.opacity = view.transparent ? 0.55 : 1;
    backMaterial.opacity = view.transparent ? 0.55 : 1;
    frontMaterial.depthWrite = !view.transparent;

    creaseLines.visible = view.showCreaseLines;
    wireLines.visible = view.wireframe;
    if (view.wireframe) {
      wireLines.geometry.dispose();
      wireLines.geometry = new THREE.WireframeGeometry(geometry);
    }
    renderer.shadowMap.enabled = view.shadows;
    frontMesh.castShadow = view.shadows;
    ground.visible = view.shadows;
    scene.background = null;
  }

  /** Distance at which a sphere of `radius` fills a comfortable share of the view. */
  function fitDistance(): number {
    const halfFov = (camera.fov * Math.PI) / 360;
    return Math.max(radius / Math.tan(halfFov) * 1.35, 0.4);
  }

  function frameCamera(): void {
    const distance = fitDistance();
    camera.position.set(distance * 0.52, -distance * 0.72, distance * 0.45);
    camera.lookAt(0, 0, 0);
    controls.target.set(0, 0, 0);
    controls.update();
  }

  function setCameraPreset(preset: CameraPreset): void {
    const d = fitDistance();
    switch (preset) {
      case 'front': camera.position.set(0, -d, 0); break;
      case 'back': camera.position.set(0, d, 0); break;
      case 'top': camera.position.set(0, 0, d); break;
      case 'left': camera.position.set(-d, 0, 0); break;
      case 'right': camera.position.set(d, 0, 0); break;
      case 'iso': camera.position.set(d * 0.58, -d * 0.58, d * 0.58); break;
      default: frameCamera(); return;
    }
    controls.target.set(0, 0, 0);
    camera.lookAt(0, 0, 0);
    controls.update();
  }

  function resize(width: number, height: number): void {
    if (width <= 0 || height <= 0) return;
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    const half = Math.max(radius * 1.25, 0.35);
    const aspect = width / height;
    orthoCamera.left = -half * aspect;
    orthoCamera.right = half * aspect;
    orthoCamera.top = half;
    orthoCamera.bottom = -half;
    orthoCamera.updateProjectionMatrix();
  }

  let useOrtho = false;
  function render(): void {
    controls.update();
    if (useOrtho) {
      orthoCamera.position.copy(camera.position);
      orthoCamera.quaternion.copy(camera.quaternion);
      orthoCamera.updateMatrixWorld();
      renderer.render(scene, orthoCamera);
    } else {
      renderer.render(scene, camera);
    }
  }

  function pick(ndc: THREE.Vector2): PickResult | null {
    if (!mesh) return null;
    raycaster.setFromCamera(ndc, useOrtho ? orthoCamera : camera);
    model.updateMatrixWorld();
    const hits = raycaster.intersectObjects([frontMesh, backMesh], false);
    const hit = hits[0];
    if (!hit || hit.faceIndex === undefined || hit.faceIndex === null || !hit.face) return null;
    const tri: number = hit.faceIndex;
    const candidates = [hit.face.a, hit.face.b, hit.face.c];
    let best = candidates[0];
    let bestD = Infinity;
    const attr = geometry.getAttribute('position') as THREE.BufferAttribute;
    for (const v of candidates) {
      const d = hit.point.distanceToSquared(
        new THREE.Vector3(attr.getX(v), attr.getY(v), attr.getZ(v)),
      );
      if (d < bestD) {
        bestD = d;
        best = v;
      }
    }
    const worldPoint = hit.point.clone();
    return {
      triangle: tri,
      vertex: best,
      point: model.worldToLocal(worldPoint.clone()),
      worldPoint,
    };
  }

  function highlight(vertex: number | null): void {
    if (vertex === null) {
      highlightMesh.visible = false;
      return;
    }
    const attr = geometry.getAttribute('position') as THREE.BufferAttribute;
    if (!attr || vertex >= attr.count) {
      highlightMesh.visible = false;
      return;
    }
    highlightMesh.position.set(attr.getX(vertex), attr.getY(vertex), attr.getZ(vertex));
    highlightMesh.visible = true;
  }

  function dispose(): void {
    controls.dispose();
    geometry.dispose();
    creaseGeometry.dispose();
    wireGeometry.dispose();
    highlightGeometry.dispose();
    frontMaterial.dispose();
    backMaterial.dispose();
    creaseMaterial.dispose();
    wireMaterial.dispose();
    highlightMaterial.dispose();
    renderer.dispose();
  }

  return {
    renderer,
    scene,
    camera,
    orthoCamera,
    controls,
    dispose,
    setMesh,
    updatePositions,
    applyView: (view) => {
      useOrtho = view.orthographic;
      const themeChanged = theme !== view.theme;
      theme = view.theme;
      applyView(view);
      // Crease colours are baked into a vertex-colour buffer, so a theme change
      // has to rebuild them.
      if (themeChanged && mesh && lastPattern) setMesh(mesh, lastPattern);
    },
    render,
    resize,
    setCameraPreset,
    pick,
    highlight,
    toModelSpace: (world) => {
      model.updateMatrixWorld();
      return model.worldToLocal(world.clone());
    },
    setControlsEnabled: (enabled) => {
      controls.enabled = enabled;
    },
    get radius() {
      return radius;
    },
  };
}

function findMeshVertex(mesh: SimulationMesh, patternVertex: number): number {
  for (let v = 0; v < mesh.vertexCount; v++) {
    if (mesh.vertexPatternIndex[v] === patternVertex) return v;
  }
  return -1;
}
